// @flow
import React from 'react';
import { withTranslation } from 'react-i18next';
import Toggle from 'react-toggled';
import { withStyles, withTheme } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';

import EditProfileModal from './EditProfileModal';
import ScribeTranscriptWordContainer from './ScribeTranscriptWord';
import ScribeWordInputContainer from './ScribeWordInputContainer';
import AsrQueueingMessageLoader from '../../components/AsrQueueingMessageLoader';
import AvatarLogo from '../../components/Icons/AvatarLogo';
import LoadingText from '../../components/LoadingText';
import { getOS } from '../../utils';
import { getText } from '../../utils/scribeUtils';

const styles = {
  loadingText: {
    height: '1em',
    fontSize: '32px',
    lineHeight: '32px',
  },
};

const isTextEqualExceptSelectedWord = (props1, props2, selectedWordIndex) => {
  const t1 = getText(
    // $FlowFixMe
    { texts: props1.texts, speechLang: props1.speechLang },
    props1.lang
  );
  const t2 = getText(
    // $FlowFixMe
    { texts: props2.texts, speechLang: props2.speechLang },
    props2.lang
  );

  const t1Words = t1.split(' ');
  const t2Words = t2.split(' ');
  if (t1Words.length !== t2Words.length) {
    return false;
  }
  if (
    t1Words.slice(0, selectedWordIndex).join(' ') !== t2Words.slice(0, selectedWordIndex).join(' ') ||
    t1Words.slice(selectedWordIndex + 1, t2Words.length).join(' ') !==
      t2Words.slice(selectedWordIndex + 1, t2Words.length).join(' ')
  ) {
    return false;
  }
  return true;
};

type Props = {
  author: string,
  authorShow: boolean,
  classes: Object,
  currentPlatform: string,
  cursors: Array<Cursor>,
  dividerHeight: number,
  // How far any scribe is known to have clicked on the transcript. If undefined - show the whole transcript regardless.
  furthestScribeIndex: number | typeof undefined,
  electronCaptionMode: boolean,
  fontSize: number,
  hideFirstInternal: boolean,
  highlighted: boolean,
  id: string,
  indexes: Object,
  isSelected: boolean,
  lang: string,
  lightView: boolean,
  muted: boolean,
  participantsHash: Array<Object>,
  scribe: boolean,
  scribeSelectTranscriptWord: Function,
  selectedWordIndex?: number,
  socketConnectionStatus: string,
  singleSpeaker: boolean,
  speaker: Object,
  speechLang: string,
  status: Object,
  t: Function,
  texts: string,
  trackName: string,
  theme: Object,
  wordBeingEdited: boolean,
  userAvaId: string,
  userName: string,
  userScribeFlag: boolean,
  speakerName: string,
};

class ScribeTranscript extends React.Component<Props> {
  shouldComponentUpdate(nextProps) {
    const { selectedWordIndex, speaker } = this.props;
    const updatedKeys = [];
    Array.from(Object.keys(nextProps)).forEach(k => {
      const { [k]: value } = this.props;
      if (nextProps[k] !== value) {
        updatedKeys.push(k);
      }
    });

    if (updatedKeys.length === 1 && updatedKeys.includes('speaker')) {
      // This is ugly, but `speaker` is recreated often and in a complex way,
      // and this trick makes it not re-render every time room-status-update is sent.
      if (
        speaker.avaId === nextProps.speaker.avaId &&
        speaker.userName === nextProps.speaker.userName &&
        speaker.userAvatar === nextProps.speaker.userAvatar &&
        speaker.theme?.dark_theme?.text_color === nextProps.speaker.theme?.dark_theme?.text_color &&
        speaker.theme?.light_theme?.text_color === nextProps.speaker.theme?.light_theme?.text_color
      )
        return false;
    }

    if (
      updatedKeys.includes('texts') &&
      isTextEqualExceptSelectedWord(nextProps, this.props, selectedWordIndex) &&
      nextProps.wordBeingEdited
    ) {
      // eslint-disable-next-line
      for (let k of ['wordBeingEdited', 'mutationsQueuedByHumanMutation', 'cursors']) {
        if (updatedKeys.includes(k) && updatedKeys.length === 2) {
          return false;
        }
      }
      if (updatedKeys.length === 1) {
        return false;
      }
    }
    if (
      updatedKeys.length === 1 &&
      (updatedKeys.includes('wordBeingEdited') || updatedKeys.includes('mutationsQueuedByHumanMutation'))
    ) {
      return false;
    }
    return true;
  }

  render() {
    const {
      author,
      authorShow,
      classes,
      cursors,
      dividerHeight,
      fontSize,
      electronCaptionMode,
      furthestScribeIndex,
      hideFirstInternal,
      highlighted,
      id,
      isSelected,
      lang,
      lightView,
      muted,
      participantsHash: participants,
      scribe,
      scribeSelectTranscriptWord,
      selectedWordIndex,
      socketConnectionStatus,
      singleSpeaker,
      speaker,
      speechLang,
      t,
      texts,
      trackName,
      theme,
      userAvaId,
      userName,
      userScribeFlag,
    } = this.props;

    let { indexes: human } = this.props;
    human = human || {};
    const participantsHash = participants ? participants.map(p => p.hash) : undefined;
    let charIndex = 0;
    // $FlowFixMe
    const text = getText({ texts, speechLang }, lang);
    const transcriptWords = text.split(' ');
    if ((muted || lightView || !isSelected) && !text) {
      return null;
    }

    const indexes = {};
    if (cursors && participantsHash) {
      cursors.forEach(cursor => {
        if (participantsHash.includes(cursor.mutatorHash)) {
          if (!indexes[cursor.index]) {
            indexes[cursor.index] = [];
          }
          indexes[cursor.index].push(cursor.mutatorHash);
        }
      });
    }
    const allowEdit = socketConnectionStatus === 'CONNECTED' && !muted && !lightView;
    const lastHuman = human[(speechLang || lang).split('-')[0]];
    const isInternalAudio =
      speaker && speaker.avaId === userAvaId && trackName && trackName.startsWith('Ava Computer Audio');

    let color = theme.palette.error.light;
    if (speaker && speaker.theme) {
      const { theme: speakerTheme } = speaker;
      color = speakerTheme[`${theme.palette.type}_theme`].text_color;
    }

    if (speaker && (speaker.avaId === userAvaId || speaker.avaId === userAvaId)) {
      color =
        theme.palette.type === 'light'
          ? theme.palette.ava.navyGroup.convoText
          : theme.palette.ava.navyGroup.convoTextDark;

      if (isInternalAudio) {
        color = theme.palette.ava.green;
      }
    }

    if (theme.palette.type === 'dark' && color === '#000000') {
      color = '#FFFFFF';
    }

    let authorName = author;
    if (speaker && speaker.userName) {
      authorName = speaker.userName;
    }
    if (speaker && (speaker.avaId === userAvaId || speaker.avaId === userAvaId)) {
      authorName = t('transcripts.you');
      if (userName === 'web guest') {
        authorName += t('transcripts.tapToSetYourName');
      }
      if (isInternalAudio) {
        authorName = t('transcripts.internalAudioAuthorName');
      }
    }

    let showAuthor = authorShow;
    if (hideFirstInternal && isInternalAudio) {
      // We show "Speakers" as first author name
      // only if it's the only author in the convo
      showAuthor = false;
    }

    return (
      <div
        style={{
          marginTop: dividerHeight,
          position: 'relative',
          lineHeight: `${fontSize}px`,
          fontSize: `${fontSize}px`,
          color:
            electronCaptionMode || (singleSpeaker && theme.palette.type === 'dark') ? theme.palette.ava.white : color,
          userSelect: electronCaptionMode ? 'none' : undefined,
          MozUserSelect: electronCaptionMode ? 'none' : undefined,
          KhtmlUserSelect: electronCaptionMode ? 'none' : undefined,
          WebkitUserSelect: electronCaptionMode ? 'none' : undefined,
          OUserSelect: electronCaptionMode ? 'none' : undefined,
          marginLeft: 'initial',
        }}
      >
        <Grid
          container
          alignItems="center"
          wrap="nowrap"
          style={{
            marginTop: showAuthor && speaker && speaker.userAvatar !== '' && !isInternalAudio ? `${fontSize / 5}` : '0',
          }}
        >
          <Hidden xsDown>
            <Grid
              item
              xs="auto"
              style={{
                flexBasis: 0,
                marginRight: !electronCaptionMode ? '5px' : 'initial',
              }}
            >
              {!electronCaptionMode && showAuthor && speaker && speaker.userAvatar && !isInternalAudio && (
                <Avatar
                  key="avatar"
                  src={speaker.userAvatar}
                  alt="speaker_photo"
                  style={{
                    width: fontSize * 2,
                    height: fontSize * 2,
                  }}
                />
              )}
              {!electronCaptionMode && showAuthor && speaker && !speaker.userAvatar && !isInternalAudio && (
                <AvatarLogo color={color} width={fontSize * 2} height={fontSize * 2} />
              )}
            </Grid>
          </Hidden>
          <Grid item xs="auto" css={{ lineHeight: 1 }}>
            {showAuthor && (
              <Toggle>
                {({ on, getTogglerProps, setOff }) => (
                  <>
                    <Typography
                      variant="h5"
                      style={{
                        color,
                        fontWeight: 'bold',
                        fontSize,
                        lineHeight: `${fontSize}px`,
                        outline: 'none',
                        display: 'inline-block',
                        cursor: speaker && speaker.avaId === userAvaId && !isInternalAudio && 'pointer',
                      }}
                      {...getTogglerProps()}
                    >
                      <strong key="name">{authorName}</strong>
                    </Typography>
                    {speaker && speaker.avaId && speaker.avaId === userAvaId && !isInternalAudio ? (
                      <EditProfileModal open={on} close={setOff} />
                    ) : (
                      <br />
                    )}
                  </>
                )}
              </Toggle>
            )}
          </Grid>
        </Grid>

        <div
          style={{
            height: electronCaptionMode ? Math.floor(fontSize * 0.6) : Math.floor(fontSize * 0.8),
          }}
        />

        <Grid container wrap="nowrap">
          <Grid item xs={12}>
            {((!muted && !lightView) || text) &&
              transcriptWords.map((wordText, i) => {
                if (!scribe && furthestScribeIndex !== undefined && i >= furthestScribeIndex) {
                  if (i === furthestScribeIndex) return <LoadingText key="loading" className={classes.loadingText} />;
                  return null;
                }
                const wordComp =
                  i === selectedWordIndex && allowEdit && isSelected ? (
                    // eslint-disable-next-line
                    <ScribeWordInputContainer key={`${i}-${wordText}`} />
                  ) : (
                    <ScribeTranscriptWordContainer
                      word={wordText}
                      cursor={userScribeFlag && indexes[i]}
                      participants={participants}
                      transcriptId={id}
                      wordIndex={i}
                      highlighted={highlighted}
                      allowEdit={allowEdit}
                      scribe={scribe}
                      unlocked={charIndex + wordText.length > lastHuman}
                      scribeSelectTranscriptWord={scribeSelectTranscriptWord}
                      // eslint-disable-next-line
                      key={`${i}-${wordText}`}
                    />
                  );
                charIndex += wordText.length + 1;
                return wordComp;
              })}
            <AsrQueueingMessageLoader transcriptId={id} />
          </Grid>
        </Grid>
      </div>
    );
  }
}
export default withTranslation()(withStyles(styles)(withTheme(ScribeTranscript)));
